import { canonicalJson, guardCredentialScopeAllowsGate, guardEventSigningPayload } from "../shared/guard";
import { guardBundlePayloadSchema } from "../shared/domain";

export type GuardCredential = {
  id: string;
  credentialType: string;
  sourceType: string;
  sourceId: string;
  status: string;
  validFrom: number;
  validUntil: number;
  displayLabel: string;
  unitLabel?: string;
  version: number;
  entryMode?: string;
  scope?: { gateScope?: unknown; gateScopes?: unknown; [key: string]: unknown };
  vehicle?: { make: string; model: string; color: string };
  plateHash?: string;
  rfidHash?: string;
};

export type GuardBundle = {
  schemaVersion: number;
  bundleId: string;
  tenantId: string;
  deviceId: string;
  sessionId: string;
  gateScope: string;
  policyVersion: number;
  issuedAt: number;
  expiresAt: number;
  serverTime: number;
  keyId: string;
  acceptedKeyIds: string[];
  maxClockSkewSeconds: number;
  allowedActions: string[];
  requireLocalIdCheck: boolean;
  credentials: GuardCredential[];
  vehicles: GuardCredential[];
};

export type GuardQueuedEvent = {
  eventId: string;
  deviceSequence: number;
  sessionId: string;
  bundleId: string;
  credentialId?: string;
  credentialType?: string;
  sourceId?: string;
  credentialVersion?: number;
  action: "verify" | "check_in" | "deny" | "checkout" | "hold" | "override";
  gateScope: string;
  direction: "entry" | "exit";
  localWallTime: number;
  localMonotonicMs: number;
  bestKnownServerTime: number;
  reason?: string;
  localIdChecked: boolean;
  overrideAction?: "check_in" | "checkout" | "deny";
  overrideGrantId?: string;
  overrideGrantToken?: string;
  nonceHash?: string;
  payloadHash: string;
  signature: string;
  queueStatus?: "failed";
  queueReason?: string;
};

type GuardMeta = {
  tenantId?: string;
  bundle?: GuardBundle;
  bundleToken?: string;
  deviceId?: string;
  deviceEnrolled?: boolean;
  sessionId?: string;
  guardUserId?: string;
  devicePublicJwk?: JsonWebKey;
  devicePrivateKey?: CryptoKey;
  pendingEnrollment?: { enrollmentToken?: string; setupCode?: string };
  pendingEnrollmentPreview?: { deviceLabel: string; gateScope: string; mode: "new" | "repair"; status: string; expiresAt: number };
  pendingEnrollmentExpiresAt?: number;
  signingKeys?: Array<{ keyId: string; publicJwk: JsonWebKey; status?: string; notBefore?: number; notAfter?: number | null }>;
  nextSequence?: number;
  lastLocalWallTime?: number;
  lastServerTime?: number;
};

const databaseName = "two-carats-guard";
const databaseVersion = 2;
const metaStore = "meta";
const eventStore = "events";
const guardIntakeSessionStorageKey = "two-carats-guard-intake-session";
let storageGeneration = 0;
let syncController: AbortController | undefined;

function clearGuardSessionScopedStorage() {
  try { sessionStorage.removeItem(guardIntakeSessionStorageKey); } catch { /* Session storage is only a refresh convenience. */ }
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, databaseVersion);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(metaStore)) request.result.createObjectStore(metaStore);
      if (!request.result.objectStoreNames.contains(eventStore)) request.result.createObjectStore(eventStore, { keyPath: "eventId" });
    };
    request.onblocked = () => reject(new Error("GUARD_STORAGE_BLOCKED"));
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => reject(request.error ?? new Error("GUARD_STORAGE_UNAVAILABLE"));
  });
}

async function readMeta(): Promise<GuardMeta> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(metaStore, "readonly");
    const request = transaction.objectStore(metaStore).get("state");
    let value: GuardMeta = {};
    request.onsuccess = () => { value = (request.result as GuardMeta | undefined) ?? {}; };
    request.onerror = () => { db.close(); reject(request.error); };
    transaction.oncomplete = () => { db.close(); resolve(value); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}

async function updateMeta(mutator: (meta: GuardMeta) => GuardMeta) {
  const db = await openDatabase();
  return new Promise<GuardMeta>((resolve, reject) => {
    const transaction = db.transaction(metaStore, "readwrite");
    const store = transaction.objectStore(metaStore);
    const request = store.get("state");
    let result: GuardMeta | undefined;
    let failure: Error | undefined;
    request.onsuccess = () => {
      try {
        result = mutator((request.result as GuardMeta | undefined) ?? {});
        store.put(result, "state");
      } catch (error) {
        failure = error instanceof Error ? error : new Error("GUARD_STORAGE_CONFLICT");
        transaction.abort();
      }
    };
    request.onerror = () => { failure = request.error ?? new Error("GUARD_STORAGE_UNAVAILABLE"); transaction.abort(); };
    transaction.oncomplete = () => { db.close(); result ? resolve(result) : reject(failure ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
    transaction.onerror = () => { db.close(); reject(failure ?? transaction.error ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
    transaction.onabort = () => { db.close(); reject(failure ?? new Error("GUARD_STORAGE_CONFLICT")); };
  });
}

async function updateMetaWithEventCount(mutator: (meta: GuardMeta, eventCount: number) => GuardMeta) {
  const db = await openDatabase();
  return new Promise<GuardMeta>((resolve, reject) => {
    const transaction = db.transaction([metaStore, eventStore], "readwrite");
    const metaStoreRef = transaction.objectStore(metaStore);
    const metaRequest = metaStoreRef.get("state");
    let result: GuardMeta | undefined;
    let failure: Error | undefined;
    metaRequest.onsuccess = () => {
      const eventsRequest = transaction.objectStore(eventStore).getAllKeys();
      eventsRequest.onsuccess = () => {
        try {
          result = mutator((metaRequest.result as GuardMeta | undefined) ?? {}, eventsRequest.result.length);
          metaStoreRef.put(result, "state");
        } catch (error) {
          failure = error instanceof Error ? error : new Error("GUARD_STORAGE_CONFLICT");
          transaction.abort();
        }
      };
      eventsRequest.onerror = () => { failure = eventsRequest.error ?? new Error("GUARD_STORAGE_UNAVAILABLE"); transaction.abort(); };
    };
    metaRequest.onerror = () => { failure = metaRequest.error ?? new Error("GUARD_STORAGE_UNAVAILABLE"); transaction.abort(); };
    transaction.oncomplete = () => { db.close(); result ? resolve(result) : reject(failure ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
    transaction.onerror = () => { db.close(); reject(failure ?? transaction.error ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
    transaction.onabort = () => { db.close(); reject(failure ?? new Error("GUARD_STORAGE_CONFLICT")); };
  });
}

async function claimDeviceKey(deviceId: string, candidate: GuardMeta) {
  const db = await openDatabase();
  return new Promise<GuardMeta>((resolve, reject) => {
    const transaction = db.transaction(metaStore, "readwrite");
    const store = transaction.objectStore(metaStore);
    const request = store.get("state");
    let result: GuardMeta | undefined;
    request.onsuccess = () => {
      const current = (request.result as GuardMeta | undefined) ?? {};
      if (current.deviceId === deviceId && current.devicePrivateKey && current.devicePublicJwk) {
        result = current;
      } else if (current.deviceId && current.deviceId !== deviceId) {
        result = current;
      } else {
        result = candidate;
        store.put(candidate, "state");
      }
    };
    request.onerror = () => { db.close(); reject(request.error ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
    transaction.oncomplete = () => {
      db.close();
      result ? resolve(result) : reject(new Error("GUARD_STORAGE_UNAVAILABLE"));
    };
    transaction.onerror = () => { db.close(); reject(transaction.error ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
  });
}

export async function getGuardState() {
  const events = await listStoredEvents();
  let meta = await readMeta();
  if (meta.pendingEnrollmentExpiresAt && meta.pendingEnrollmentExpiresAt <= Math.floor(Date.now() / 1000)) {
    meta = await updateMeta((current) => {
      if (!current.pendingEnrollmentExpiresAt || current.pendingEnrollmentExpiresAt > Math.floor(Date.now() / 1000)) return current;
      if (events.length) return { ...current, pendingEnrollment: undefined, pendingEnrollmentPreview: undefined, pendingEnrollmentExpiresAt: undefined };
      if (current.deviceId || current.sessionId || current.bundle) return { ...current, pendingEnrollment: undefined, pendingEnrollmentPreview: undefined, pendingEnrollmentExpiresAt: undefined };
      return { ...current, devicePrivateKey: undefined, devicePublicJwk: undefined, pendingEnrollment: undefined, pendingEnrollmentPreview: undefined, pendingEnrollmentExpiresAt: undefined, deviceEnrolled: false };
    });
  }
  return { meta, events };
}

export async function clearGuardStorage() {
  storageGeneration += 1;
  syncController?.abort();
  clearGuardSessionScopedStorage();
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([metaStore, eventStore], "readwrite");
    transaction.objectStore(metaStore).clear();
    transaction.objectStore(eventStore).clear();
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}

export async function clearGuardBundle() {
  await updateMeta((meta) => ({ ...meta, bundle: undefined, bundleToken: undefined, signingKeys: undefined, lastServerTime: undefined }));
}

export async function ensureGuardDeviceKey(deviceId: string) {
  let meta = await readMeta();
  if (meta.deviceId === deviceId && meta.devicePrivateKey && meta.devicePublicJwk) return meta;
  if (meta.deviceId && meta.deviceId !== deviceId) {
    const existingEvents = await listStoredEvents();
    if (meta.bundle || meta.sessionId || existingEvents.length) throw new Error("GUARD_DEVICE_CHANGE_REQUIRES_WIPE");
    await clearGuardStorage();
    meta = await readMeta();
  }
  const pair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"]);
  const devicePublicJwk = await crypto.subtle.exportKey("jwk", pair.publicKey);
  const next = { ...meta, deviceId, deviceEnrolled: false, devicePrivateKey: pair.privateKey, devicePublicJwk, nextSequence: meta.nextSequence ?? 0 };
  const claimed = await claimDeviceKey(deviceId, next);
  if (claimed.deviceId !== deviceId || !claimed.devicePrivateKey || !claimed.devicePublicJwk) throw new Error("GUARD_DEVICE_KEY_CONFLICT");
  return claimed;
}

export type GuardEnrollmentSecret = { enrollmentToken?: string; setupCode?: string };
export type GuardEnrollmentPreview = { deviceLabel: string; gateScope: string; mode: "new" | "repair"; status: string; expiresAt: number };

export async function setPendingGuardEnrollment(secret: GuardEnrollmentSecret, preview: GuardEnrollmentPreview) {
  await updateMeta((current) => ({ ...current, pendingEnrollment: secret, pendingEnrollmentPreview: preview, pendingEnrollmentExpiresAt: preview.expiresAt }));
}

export async function ensureGuardEnrollmentKey(secret: GuardEnrollmentSecret, mode: "new" | "repair" = "new", expiresAt?: number) {
  const state = await getGuardState();
  if (state.events.length) throw new Error("GUARD_QUEUED_EVENTS_REQUIRE_SYNC");
  let meta = state.meta;
  const pendingPreview = meta.pendingEnrollmentPreview;
  const sameSecret = canonicalJson(meta.pendingEnrollment) === canonicalJson(secret);
  if (sameSecret && meta.devicePrivateKey && meta.devicePublicJwk && !meta.deviceId) {
    if (expiresAt && meta.pendingEnrollmentExpiresAt !== expiresAt) return updateMeta((current) => ({ ...current, pendingEnrollmentExpiresAt: expiresAt }));
    return meta;
  }
  if (meta.deviceId && mode !== "repair") throw new Error("GUARD_DEVICE_ALREADY_ENROLLED");
  if (meta.deviceId || meta.sessionId || meta.bundle || meta.devicePrivateKey) {
    meta = await updateMeta((current) => ({
      ...current,
      tenantId: undefined,
      deviceId: undefined,
      deviceEnrolled: false,
      sessionId: undefined,
      guardUserId: undefined,
      bundle: undefined,
      bundleToken: undefined,
      signingKeys: undefined,
      devicePublicJwk: undefined,
      devicePrivateKey: undefined,
      pendingEnrollment: undefined,
      pendingEnrollmentPreview: undefined,
      pendingEnrollmentExpiresAt: undefined,
      nextSequence: undefined,
      lastLocalWallTime: undefined,
      lastServerTime: undefined,
    }));
    clearGuardSessionScopedStorage();
  }
  const pair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"]);
  const devicePublicJwk = await crypto.subtle.exportKey("jwk", pair.publicKey);
  return updateMeta((current) => ({
    ...current,
    deviceEnrolled: false,
    devicePrivateKey: pair.privateKey,
    devicePublicJwk,
    pendingEnrollment: secret,
    pendingEnrollmentPreview: current.pendingEnrollmentPreview ?? pendingPreview,
    pendingEnrollmentExpiresAt: expiresAt,
    nextSequence: current.nextSequence ?? 0,
  }));
}

export async function clearPendingGuardEnrollment() {
  const eventCount = (await listQueuedEvents()).length;
  await updateMeta((current) => {
    if (current.deviceId || current.sessionId || current.bundle || eventCount) return { ...current, pendingEnrollment: undefined, pendingEnrollmentPreview: undefined, pendingEnrollmentExpiresAt: undefined };
    return { ...current, devicePrivateKey: undefined, devicePublicJwk: undefined, pendingEnrollment: undefined, pendingEnrollmentPreview: undefined, pendingEnrollmentExpiresAt: undefined, deviceEnrolled: false };
  });
}

function base64UrlBytes(value: string) {
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/") + "=".repeat((4 - value.length % 4) % 4);
  return Uint8Array.from(atob(normalized), (character) => character.charCodeAt(0));
}

function base64UrlJson(value: string) {
  return JSON.parse(new TextDecoder().decode(base64UrlBytes(value))) as Record<string, unknown>;
}

function splitSignedToken(token: string) {
  const parts = token.trim().split(".");
  if (parts.length !== 3) throw new Error("TOKEN_INVALID");
  return { header: base64UrlJson(parts[0]), payload: base64UrlJson(parts[1]), signature: base64UrlBytes(parts[2]), input: `${parts[0]}.${parts[1]}` };
}

async function verifySignedToken(token: string, keys: Array<{ keyId: string; publicJwk: JsonWebKey; status?: string; notBefore?: number; notAfter?: number | null }>, expectedType?: string) {
  const signed = splitSignedToken(token);
  if (signed.header.alg !== "ES256" || (expectedType && signed.header.typ !== expectedType)) throw new Error("TOKEN_TYPE_INVALID");
  const keyId = String(signed.header.kid ?? "");
  const key = keys.find((item) => item.keyId === keyId);
  if (!key) throw new Error("SIGNING_KEY_UNKNOWN");
  if (key.status && !["active", "retired"].includes(key.status)) throw new Error("SIGNING_KEY_REVOKED");
  const issuedAt = Number(signed.payload.issuedAt ?? signed.payload.iat ?? Math.floor(Date.now() / 1000));
  if ((key.notBefore ?? 0) > issuedAt || (key.notAfter != null && key.notAfter < issuedAt)) throw new Error("SIGNING_KEY_OUTSIDE_VALIDITY");
  const publicKey = await crypto.subtle.importKey("jwk", key.publicJwk, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
  const valid = await crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, publicKey, signed.signature, new TextEncoder().encode(signed.input));
  if (!valid) throw new Error("SIGNATURE_INVALID");
  return signed.payload;
}

async function sha256(value: string) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function encodeSignature(value: ArrayBuffer) {
  let binary = "";
  for (const byte of new Uint8Array(value)) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

export async function saveGuardBundle(bundleToken: string, signingKeys: Array<{ keyId: string; publicJwk: JsonWebKey; status?: string; notBefore?: number; notAfter?: number | null }>) {
  const meta = await readMeta();
  const tokenHeader = splitSignedToken(bundleToken).header;
  const payload = await verifySignedToken(bundleToken, signingKeys, "GUARD_BUNDLE");
  const parsed = guardBundlePayloadSchema.safeParse(payload);
  if (!parsed.success) throw new Error("GUARD_BUNDLE_INVALID");
  const bundle = parsed.data as GuardBundle;
  if (bundle.schemaVersion !== 1 || bundle.expiresAt <= Math.floor(Date.now() / 1000)) throw new Error("GUARD_BUNDLE_EXPIRED");
  if (String(tokenHeader.kid ?? "") !== bundle.keyId) throw new Error("GUARD_BUNDLE_KEY_MISMATCH");
  if (!bundle.acceptedKeyIds.includes(String(tokenHeader.kid ?? ""))) throw new Error("SIGNING_KEY_NOT_ACCEPTED");
  if (meta.deviceId && bundle.deviceId !== meta.deviceId) throw new Error("GUARD_BUNDLE_DEVICE_INVALID");
  if (meta.sessionId && bundle.sessionId !== meta.sessionId) throw new Error("GUARD_BUNDLE_SESSION_INVALID");
  if (meta.tenantId && bundle.tenantId !== meta.tenantId) throw new Error("GUARD_BUNDLE_TENANT_INVALID");
  await updateMeta((current) => {
    if (current.deviceId !== meta.deviceId || current.sessionId !== meta.sessionId) throw new Error("GUARD_STORAGE_CONFLICT");
    if (current.tenantId && current.tenantId !== bundle.tenantId) throw new Error("GUARD_STORAGE_CONFLICT");
    return { ...current, tenantId: bundle.tenantId, bundle, bundleToken, signingKeys, lastServerTime: Math.max(current.lastServerTime ?? 0, bundle.serverTime) };
  });
  return bundle;
}

export async function readGuardBundle() {
  const meta = await readMeta();
  if (!meta.bundle || !meta.bundleToken || !meta.signingKeys) return undefined;
  if (meta.tenantId && meta.bundle.tenantId !== meta.tenantId) return undefined;
  if (meta.deviceId && meta.bundle.deviceId !== meta.deviceId) return undefined;
  if (meta.sessionId && meta.bundle.sessionId !== meta.sessionId) return undefined;
  try {
    const tokenHeader = splitSignedToken(meta.bundleToken).header;
    const payload = await verifySignedToken(meta.bundleToken, meta.signingKeys, "GUARD_BUNDLE");
    const parsed = guardBundlePayloadSchema.safeParse(payload);
    if (!parsed.success || parsed.data.bundleId !== meta.bundle.bundleId || parsed.data.keyId !== meta.bundle.keyId || String(tokenHeader.kid ?? "") !== parsed.data.keyId || !parsed.data.acceptedKeyIds.includes(String(tokenHeader.kid ?? ""))) return undefined;
    return parsed.data as GuardBundle;
  } catch {
    return undefined;
  }
}

export async function parsePresentedCredential(tokenOrCode: string) {
  const value = tokenOrCode.trim();
  const bundle = await readGuardBundle();
  if (!bundle) throw new Error("GUARD_BUNDLE_REQUIRED");
  if (bundle.expiresAt <= Math.floor(Date.now() / 1000)) throw new Error("GUARD_BUNDLE_EXPIRED");
  const meta = await readMeta();
  let credential: GuardCredential | undefined;
  let nonceHash: string | undefined;
  if (value.includes(".")) {
    const tokenHeader = splitSignedToken(value).header;
    const payload = await verifySignedToken(value, meta.signingKeys ?? [], "JWT");
    const now = Math.floor(Date.now() / 1000);
    if (payload.tenantId !== bundle.tenantId) throw new Error("TOKEN_TENANT_INVALID");
    if (!bundle.acceptedKeyIds.includes(String(tokenHeader.kid ?? ""))) throw new Error("SIGNING_KEY_NOT_ACCEPTED");
    if (!payload.scope || typeof payload.scope !== "object" || !guardCredentialScopeAllowsGate(payload.scope as { gateScope?: unknown; gateScopes?: unknown }, bundle.gateScope)) throw new Error("GATE_SCOPE_NOT_ALLOWED");
    if (!payload.credentialId || !Number.isInteger(Number(payload.version)) || typeof payload.nonce !== "string") throw new Error("TOKEN_INVALID");
    if (Number(payload.exp ?? 0) < now - bundle.maxClockSkewSeconds) throw new Error("TOKEN_EXPIRED");
    if (Number(payload.iat ?? 0) > now + bundle.maxClockSkewSeconds) throw new Error("TOKEN_NOT_YET_VALID");
    credential = bundle.credentials.find((item) => item.id === payload.credentialId && item.version === Number(payload.version));
    nonceHash = await sha256(payload.nonce);
  } else {
    const normalized = value.toLowerCase();
    credential = [...bundle.credentials, ...bundle.vehicles].find((item) => {
      const publicCode = typeof item.scope?.publicCode === "string" ? item.scope.publicCode : undefined;
      const publicNumber = typeof item.scope?.publicNumber === "string" ? item.scope.publicNumber : undefined;
      const trackingReference = typeof item.scope?.trackingReference === "string" ? item.scope.trackingReference : undefined;
      return [item.id, item.sourceId, item.displayLabel, publicCode, publicNumber, trackingReference]
        .filter((candidate): candidate is string => Boolean(candidate))
        .some((candidate) => candidate.toLowerCase() === normalized);
    });
    if (!credential) {
      const plateHash = await sha256(value.toUpperCase());
      credential = bundle.vehicles.find((item) => item.plateHash === plateHash);
    }
    if (!credential) {
      const rfidHash = await sha256(value);
      credential = bundle.vehicles.find((item) => item.rfidHash === rfidHash);
    }
  }
  const now = Math.floor(Date.now() / 1000);
  if (!credential || !guardCredentialScopeAllowsGate(credential.scope, bundle.gateScope) || credential.status !== "active") throw new Error(!credential || credential.status !== "active" ? "CREDENTIAL_NOT_AVAILABLE" : "GATE_SCOPE_NOT_ALLOWED");
  if (credential.validFrom > now + bundle.maxClockSkewSeconds || credential.validUntil < now - bundle.maxClockSkewSeconds) throw new Error("CREDENTIAL_OUTSIDE_VALIDITY_WINDOW");
  return { credential, nonceHash };
}

async function reserveGuardEvent(input: Omit<GuardQueuedEvent, "eventId" | "deviceSequence" | "sessionId" | "bundleId" | "payloadHash" | "signature" | "localWallTime" | "localMonotonicMs" | "bestKnownServerTime" | "queueStatus" | "queueReason">) {
  const db = await openDatabase();
  return new Promise<{ meta: GuardMeta; event: Omit<GuardQueuedEvent, "payloadHash" | "signature"> }>((resolve, reject) => {
    const transaction = db.transaction(metaStore, "readwrite");
    const store = transaction.objectStore(metaStore);
    const request = store.get("state");
    let reservation: { meta: GuardMeta; event: Omit<GuardQueuedEvent, "payloadHash" | "signature"> } | undefined;
    request.onsuccess = () => {
      const meta = (request.result as GuardMeta | undefined) ?? {};
      if (!meta.devicePrivateKey || !meta.bundle || !meta.deviceId || !meta.sessionId) { transaction.abort(); reject(new Error("GUARD_DEVICE_NOT_PAIRED")); return; }
      const now = Math.floor(Date.now() / 1000);
      if (meta.bundle.expiresAt <= now) { transaction.abort(); reject(new Error("GUARD_BUNDLE_EXPIRED")); return; }
      if (meta.lastLocalWallTime !== undefined && now < meta.lastLocalWallTime) { transaction.abort(); reject(new Error("GUARD_CLOCK_ROLLBACK")); return; }
      const bestKnownServerTime = Math.max(meta.lastServerTime ?? meta.bundle.serverTime, meta.bundle.serverTime + Math.max(0, Math.floor((Date.now() - meta.bundle.issuedAt * 1_000) / 1_000)));
      const event = {
        ...input,
        bundleId: meta.bundle.bundleId,
        sessionId: meta.sessionId,
        eventId: crypto.randomUUID(),
        deviceSequence: (meta.nextSequence ?? 0) + 1,
        localWallTime: now,
        localMonotonicMs: Math.floor(performance.now()),
        bestKnownServerTime,
        localIdChecked: input.localIdChecked ?? false,
      } as Omit<GuardQueuedEvent, "payloadHash" | "signature">;
      store.put({ ...meta, nextSequence: event.deviceSequence, lastLocalWallTime: now, lastServerTime: bestKnownServerTime }, "state");
      reservation = { meta, event };
    };
    request.onerror = () => reject(request.error ?? new Error("GUARD_STORAGE_UNAVAILABLE"));
    transaction.oncomplete = () => { db.close(); reservation ? resolve(reservation) : reject(new Error("GUARD_STORAGE_UNAVAILABLE")); };
    transaction.onerror = () => { db.close(); reject(transaction.error ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
  });
}

async function persistSignedGuardEvent(event: GuardQueuedEvent, expected: { deviceId: string; sessionId: string; bundleId: string; devicePublicJwk?: JsonWebKey }, generation: number) {
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([metaStore, eventStore], "readwrite");
    const metaRequest = transaction.objectStore(metaStore).get("state");
    let failure: Error | undefined;
    metaRequest.onsuccess = () => {
      const meta = metaRequest.result as GuardMeta | undefined;
      if (generation !== storageGeneration || !meta || meta.deviceId !== expected.deviceId || meta.sessionId !== expected.sessionId || meta.bundle?.bundleId !== expected.bundleId || canonicalJson(meta.devicePublicJwk) !== canonicalJson(expected.devicePublicJwk)) {
        failure = new Error("GUARD_STORAGE_CLEARED");
        transaction.abort();
        return;
      }
      transaction.objectStore(eventStore).put(event);
    };
    metaRequest.onerror = () => { failure = metaRequest.error ?? new Error("GUARD_STORAGE_UNAVAILABLE"); transaction.abort(); };
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(failure ?? transaction.error ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
    transaction.onabort = () => { db.close(); reject(failure ?? new Error("GUARD_STORAGE_CLEARED")); };
  });
}

export async function queueGuardEvent(input: Omit<GuardQueuedEvent, "eventId" | "deviceSequence" | "sessionId" | "bundleId" | "payloadHash" | "signature" | "localWallTime" | "localMonotonicMs" | "bestKnownServerTime" | "queueStatus" | "queueReason">) {
  const generation = storageGeneration;
  const { meta, event } = await reserveGuardEvent(input);
  const payload = guardEventSigningPayload(event as unknown as Record<string, unknown>);
  const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, meta.devicePrivateKey!, new TextEncoder().encode(payload));
  const signed = { ...event, payloadHash: await sha256(payload), signature: encodeSignature(signature) } as GuardQueuedEvent;
  await persistSignedGuardEvent(signed, { deviceId: meta.deviceId!, sessionId: meta.sessionId!, bundleId: event.bundleId, devicePublicJwk: meta.devicePublicJwk }, generation);
  return signed;
}

async function listStoredEvents() {
  const db = await openDatabase();
  return new Promise<GuardQueuedEvent[]>((resolve, reject) => {
    const transaction = db.transaction(eventStore, "readonly");
    const request = transaction.objectStore(eventStore).getAll();
    let events: GuardQueuedEvent[] = [];
    request.onsuccess = () => { events = (request.result as GuardQueuedEvent[]).sort((left, right) => left.deviceSequence - right.deviceSequence); };
    request.onerror = () => { db.close(); reject(request.error); };
    transaction.oncomplete = () => { db.close(); resolve(events); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}

export async function listQueuedEvents() {
  return (await listStoredEvents()).filter((event) => event.queueStatus !== "failed");
}

export async function clearFailedGuardEvents() {
  const events = await listStoredEvents();
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(eventStore, "readwrite"); const store = transaction.objectStore(eventStore);
    for (const event of events) if (event.queueStatus === "failed") store.delete(event.eventId);
    transaction.oncomplete = () => { db.close(); resolve(); }; transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}

type GuardSyncResult = { eventId: string; status: string; reason?: string };

async function applyGuardSyncResults(events: GuardQueuedEvent[], results: GuardSyncResult[], expected: { deviceId: string; sessionId: string }) {
  const resultById = new Map(results.map((result) => [result.eventId, result]));
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([metaStore, eventStore], "readwrite");
    const metaRequest = transaction.objectStore(metaStore).get("state");
    metaRequest.onsuccess = () => {
      const meta = metaRequest.result as GuardMeta | undefined;
      if (!meta || meta.deviceId !== expected.deviceId || meta.sessionId !== expected.sessionId) {
        transaction.abort();
        return;
      }
      const store = transaction.objectStore(eventStore);
      for (const event of events) {
        const result = resultById.get(event.eventId);
        if (result && ["applied", "duplicate"].includes(result.status)) store.delete(event.eventId);
        if (result && ["rejected", "conflict"].includes(result.status)) store.put({ ...event, queueStatus: "failed", queueReason: result.reason ?? "GUARD_EVENT_REJECTED" });
      }
    };
    metaRequest.onerror = () => { transaction.abort(); };
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(transaction.error ?? new Error("GUARD_STORAGE_UNAVAILABLE")); };
    transaction.onabort = () => { db.close(); reject(new Error("GUARD_STORAGE_CLEARED")); };
  });
}

async function syncGuardEventsInternal() {
  const generation = storageGeneration;
  const controller = new AbortController();
  syncController = controller;
  try {
    const meta = await readMeta();
    if (!meta.deviceId || !meta.sessionId) throw new Error("GUARD_SESSION_REQUIRED");
    const events = await listQueuedEvents();
    if (!events.length) return [];
    const allResults: GuardSyncResult[] = [];
    for (let start = 0; start < events.length; start += 100) {
      if (generation !== storageGeneration) throw new Error("GUARD_STORAGE_CLEARED");
      const chunk = events.slice(start, start + 100);
      const idempotencyKey = `guard-sync-${meta.deviceId}-${meta.sessionId}-${chunk[0].deviceSequence}-${chunk.at(-1)!.deviceSequence}-${await sha256(canonicalJson(chunk))}`;
      const response = await fetch("/api/guard/events/sync", { method: "POST", signal: controller.signal, headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey }, body: JSON.stringify({ deviceId: meta.deviceId, sessionId: meta.sessionId, events: chunk }) });
      const body = await response.json() as { results?: GuardSyncResult[]; error?: string };
      if (!response.ok) throw new Error(body.error ?? "GUARD_SYNC_FAILED");
      const results = body.results ?? [];
      await applyGuardSyncResults(chunk, results, { deviceId: meta.deviceId, sessionId: meta.sessionId });
      allResults.push(...results);
    }
    return allResults;
  } finally {
    if (syncController === controller) syncController = undefined;
  }
}

let syncInFlight: Promise<GuardSyncResult[]> | undefined;

export function syncGuardEvents() {
  if (!syncInFlight) syncInFlight = syncGuardEventsInternal().finally(() => { syncInFlight = undefined; });
  return syncInFlight;
}

export async function setGuardSession(deviceId: string, sessionId: string, guardUserId: string, signingKeys: Array<{ keyId: string; publicJwk: JsonWebKey; status?: string; notBefore?: number; notAfter?: number | null }>, nextSequence = 0, expectedPublicJwk?: JsonWebKey, tenantId?: string) {
  await updateMetaWithEventCount((current, eventCount) => {
    if (expectedPublicJwk && canonicalJson(current.devicePublicJwk) !== canonicalJson(expectedPublicJwk)) throw new Error("GUARD_DEVICE_KEY_CHANGED");
    if (tenantId && current.tenantId && current.tenantId !== tenantId) throw new Error("GUARD_TENANT_CHANGED");
    if (current.deviceId && current.deviceId !== deviceId) throw new Error("GUARD_DEVICE_CHANGE_REQUIRES_WIPE");
    if (current.sessionId !== sessionId && (current.bundle || eventCount)) throw new Error("GUARD_SESSION_CHANGE_REQUIRES_WIPE");
    const sessionChanged = current.sessionId !== sessionId;
    return {
      ...current,
      tenantId: tenantId ?? current.tenantId,
      deviceId,
      deviceEnrolled: true,
      pendingEnrollment: undefined,
      pendingEnrollmentPreview: undefined,
      pendingEnrollmentExpiresAt: undefined,
      sessionId,
      guardUserId,
      bundle: sessionChanged ? undefined : current.bundle,
      bundleToken: sessionChanged ? undefined : current.bundleToken,
      signingKeys,
      lastServerTime: sessionChanged ? undefined : current.lastServerTime,
      lastLocalWallTime: sessionChanged ? undefined : current.lastLocalWallTime,
      nextSequence: Math.max(current.nextSequence ?? 0, nextSequence),
    };
  });
}

export async function prepareGuardSessionChange() {
  await updateMetaWithEventCount((current, eventCount) => {
    if (eventCount) throw new Error("GUARD_QUEUED_EVENTS_REQUIRE_SYNC");
    return { ...current, tenantId: undefined, deviceEnrolled: current.deviceEnrolled ?? Boolean(current.sessionId || current.guardUserId), sessionId: undefined, guardUserId: undefined, bundle: undefined, bundleToken: undefined, signingKeys: undefined, lastServerTime: undefined, lastLocalWallTime: undefined };
  });
  clearGuardSessionScopedStorage();
}

export async function endGuardShiftForSignOut() {
  const state = await getGuardState();
  if (!state.meta.deviceId || !state.meta.sessionId) {
    if (state.meta.pendingEnrollment) await clearPendingGuardEnrollment();
    return;
  }
  if (state.events.length) throw new Error("GUARD_QUEUED_EVENTS_REQUIRE_SYNC");
  const response = await fetch(`/api/guard/devices/${encodeURIComponent(state.meta.deviceId)}/end-shift`, { method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() }, credentials: "same-origin", body: JSON.stringify({ sessionId: state.meta.sessionId }) });
  const body = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(body.error ?? "GUARD_SHIFT_END_FAILED");
  await prepareGuardSessionChange();
}

export async function getGuardMeta() {
  return readMeta();
}

export function guardPayloadForDisplay(value: unknown) {
  return canonicalJson(value);
}
